import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import type { Pagination } from '@/dtos';
import type { PictogramList } from '@/dtos/Pictogram';
import axiosInstance from '@/plugins/axios';
import { createAppAsyncThunk } from '@/store/thunk';

interface IGroupedPictograms {
  tag: {
    id: string;
    name: string;
  };
  pictograms: PictogramList[];
}

interface PictogramsState {
  loading: boolean;
  list: PictogramList[];
  listGrouped: IGroupedPictograms[];
  item: PictogramList | null;
  page: number;
  pageSize: number;
  total: number;
}

const initialState: PictogramsState = {
  loading: false,
  list: [],
  listGrouped: [],
  item: null,
  page: 0,
  pageSize: 9,
  total: 0
};

export const requestPictograms = createAppAsyncThunk(
  'pictograms/getList',
  async (data: { page: number; pageSize: number; title?: string; tagId?: string }) => {
    try {
      const response = await axiosInstance.get<undefined, Pagination<PictogramList>>('/public/pictograms', {
        params: {
          page: data.page,
          pageSize: data.pageSize,
          ...(data.title ? { title: data.title } : {}),
          ...(data.tagId ? { tagId: data.tagId } : {})
        }
      });
      return response;
    } catch {
      return;
    }
  }
);
export const requestPictogramsGrouped = createAppAsyncThunk(
  'pictograms/getListGrouped',
  async (data: { page: number; pageSize: number; title?: string; tagId?: string }) => {
    try {
      const response = await axiosInstance.get<
        undefined,
        Pagination<{ tag: { id: string; name: string }; pictograms: PictogramList[] }>
      >('/public/pictograms-grouped', {
        params: {
          page: data.page,
          pageSize: data.pageSize,
          ...(data.title ? { title: data.title } : {}),
          ...(data.tagId ? { tagId: data.tagId } : {})
        }
      });
      return response;
    } catch {
      return;
    }
  }
);

export const requestPictogram = createAppAsyncThunk('pictograms/getItem', async (id: string) => {
  try {
    const response = await axiosInstance.get<undefined, PictogramList>(`/public/pictograms/${id}`);
    return response;
  } catch {
    return;
  }
});

export const pictogramsSlice = createSlice({
  name: 'pictograms',
  initialState,
  reducers: {
    setPage: (state, action: PayloadAction<number>) => {
      state.page = action.payload;
    },
    setPageSize: (state, action: PayloadAction<number>) => {
      state.pageSize = action.payload;
    },
    resetPictograms: (state) => {
      state.loading = false;
      state.list = [];
      state.listGrouped = [];
      state.item = null;
      state.page = 0;
      state.pageSize = 9;
      state.total = 0;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(requestPictograms.pending, (state) => {
        state.loading = true;
      })
      .addCase(requestPictograms.rejected, (state) => {
        state.item = null;
        state.page = 0;
        state.pageSize = 8;
        state.total = 0;
        state.loading = false;
      })
      .addCase(requestPictograms.fulfilled, (state, action) => {
        if (action.payload) {
          const isNewSearch = action.payload.page === 0;

          state.list = isNewSearch
            ? action.payload.items // reemplaza
            : [...state.list, ...action.payload.items.filter((i) => !state.list.find((l) => l.id === i.id))];

          state.total = action.payload.total;
          state.page = action.payload.page;
          state.pageSize = action.payload.pageSize;
        }
        state.loading = false;
      });
    builder
      .addCase(requestPictogramsGrouped.pending, (state) => {
        state.loading = true;
      })
      .addCase(requestPictogramsGrouped.rejected, (state) => {
        state.item = null;
        state.page = 0;
        state.pageSize = 8;
        state.total = 0;
        state.loading = false;
      })
      .addCase(requestPictogramsGrouped.fulfilled, (state, action) => {
        if (action.payload) {
          const isNewSearch = action.payload.page === 0;

          state.listGrouped = isNewSearch
            ? action.payload.items // reemplaza
            : [
                ...state.listGrouped,
                ...action.payload.items
                // .filter((i) => !state.listGrouped.find((l) => l.tag.id === i.tag.id))
              ];

          state.total = action.payload.total;
          state.page = action.payload.page;
          state.pageSize = action.payload.pageSize;
        }
        state.loading = false;
      });
    builder
      .addCase(requestPictogram.pending, (state) => {
        state.item = null;
        state.loading = true;
      })
      .addCase(requestPictogram.rejected, (state) => {
        state.loading = false;
      })
      .addCase(requestPictogram.fulfilled, (state, action) => {
        if (action.payload) {
          state.item = action.payload;
        }
        state.loading = false;
      });
  }
});

export const { setPage, setPageSize } = pictogramsSlice.actions;
